import { Request, Response } from "express";
import mongoose from "mongoose";

import {
  Attendance,
  ATTENDANCE_LOG_TYPE,
  ATTENDANCE_STATUS,
  ATTENDANCE_STATUS_CHECK_IN,
  AttendanceAudit,
  AttendanceLog,
  BreakTimer,
  Class,
  Enrollment,
  Student,
  Teacher,
} from "../models";
import {
  recordAttendanceAudit,
  refreshAttendanceSummary,
  resolveEditableState,
} from "../services/attendance-summary-service";
import { isSemesterLockedForClass } from "../utils/attendance-semester-lock";
import {
  getClassSessionForDate,
  getClassSessionForDateUTC,
  getDayBounds,
} from "../utils/class-session-utils";
import { logger } from "../utils/logger";

/** Grace period in ms after class start; check-in within this = Present, after = Late */
const LATE_GRACE_MS = 15 * 60 * 1000;

// Standard API response interface
interface APIResponse<T = unknown> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// Extended Request interface for authenticated users
interface AuthRequest extends Request {
  user?: {
    userId: string;
    role: string;
    schoolId?: string;
  };
}

class AttendanceController {
  /**
   * Mark attendance for a class (School/Teacher)
   * @route POST /api/v1/school/attendance
   * @access Private (School Admin, Teacher)
   */
  async markAttendance(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, date, attendanceRecords } = req.body;
      const userId = req.user?.userId;
      const schoolId = req.user?.schoolId;
      const userRole = req.user?.role;

      if (!schoolId || !userId) {
        res.status(400).json({
          success: false,
          message: "School ID and User ID are required",
        } as APIResponse);
        return;
      }

      if (
        !classId ||
        !date ||
        !attendanceRecords ||
        !Array.isArray(attendanceRecords)
      ) {
        res.status(400).json({
          success: false,
          message: "Class ID, date, and attendance records are required",
        } as APIResponse);
        return;
      }

      // Validate class exists
      const classItem = await Class.findOne({
        _id: classId,
        schoolId: new mongoose.Types.ObjectId(schoolId),
      });

      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
        } as APIResponse);
        return;
      }

      // Get teacher ID if user is teacher
      let teacherId: mongoose.Types.ObjectId | undefined;
      if (userRole === "teacher") {
        const teacher = await Teacher.findOne({
          userId: new mongoose.Types.ObjectId(userId),
          schoolId: new mongoose.Types.ObjectId(schoolId),
        });
        if (teacher) {
          teacherId = teacher._id;
        }
      }

      const attendanceDate = new Date(date);
      attendanceDate.setHours(0, 0, 0, 0);

      const markedAttendance = [];
      const errors = [];

      for (const record of attendanceRecords) {
        const { studentId, status, remarks } = record;

        if (!studentId || !status) {
          errors.push({
            studentId,
            error: "Student ID and status are required",
          });
          continue;
        }

        // Validate student belongs to class
        if (!classItem.studentIds?.some(id => id.toString() === studentId)) {
          errors.push({
            studentId,
            error: "Student is not enrolled in this class",
          });
          continue;
        }

        // Check if attendance already marked for this student on this date
        // eslint-disable-next-line @typescript-eslint/no-deprecated
        const existing = await Attendance.findOne({
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          classId: new mongoose.Types.ObjectId(classId),
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          studentId: new mongoose.Types.ObjectId(studentId),
          date: {
            $gte: new Date(attendanceDate),
            $lt: new Date(attendanceDate.getTime() + 24 * 60 * 60 * 1000),
          },
        });

        if (existing) {
          // Update existing attendance
          existing.status = status;
          existing.remarks = remarks;
          existing.markedBy = new mongoose.Types.ObjectId(userId);
          existing.markedAt = new Date();
          if (teacherId) existing.teacherId = teacherId;
          await existing.save();
          markedAttendance.push(existing);
        } else {
          // Create new attendance
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          const attendance = new Attendance({
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            schoolId: new mongoose.Types.ObjectId(schoolId),
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            classId: new mongoose.Types.ObjectId(classId),
            semesterId: classItem.semesterId,
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            studentId: new mongoose.Types.ObjectId(studentId),
            teacherId,
            date: attendanceDate,
            status,
            remarks,
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            markedBy: new mongoose.Types.ObjectId(userId),
            markedAt: new Date(),
          });
          await attendance.save();
          markedAttendance.push(attendance);
        }
      }

      logger.info(`Attendance marked for class: ${classId}`, {
        classId,
        date: attendanceDate,
        markedCount: markedAttendance.length,
        errorCount: errors.length,
      });

      res.status(201).json({
        success: true,
        message: `Attendance marked for ${markedAttendance.length} student(s)`,
        data: {
          attendance: markedAttendance.map(a => ({
            id: a.id,
            studentId: a.studentId,
            status: a.status,
            remarks: a.remarks,
            date: a.date,
          })),
          errors: errors.length > 0 ? errors : undefined,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Mark attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to mark attendance",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get attendance for a class (School Panel)
   * @route GET /api/v1/school/attendance
   * @access Private (School Admin, Teacher)
   */
  async getClassAttendance(req: AuthRequest, res: Response): Promise<void> {
    try {
      const {
        classId,
        date,
        startDate,
        endDate,
        page = 1,
        limit = 50,
      } = req.query;
      const schoolId = req.user?.schoolId;

      if (!schoolId) {
        res.status(400).json({
          success: false,
          message: "School ID is required",
        } as APIResponse);
        return;
      }

      if (!classId) {
        res.status(400).json({
          success: false,
          message: "Class ID is required",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        classId: new mongoose.Types.ObjectId(classId as string),
        schoolId: new mongoose.Types.ObjectId(schoolId),
      };

      if (date) {
        const attendanceDate = new Date(date as string);
        attendanceDate.setHours(0, 0, 0, 0);
        const nextDay = new Date(attendanceDate);
        nextDay.setDate(nextDay.getDate() + 1);
        filterQuery.date = { $gte: attendanceDate, $lt: nextDay };
      } else if (startDate || endDate) {
        filterQuery.date = {};
        if (startDate) {
          filterQuery.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.date.$lte = new Date(endDate as string);
        }
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const attendance = await Attendance.find(filterQuery)
        .populate("student", "fullName studentId profilePicture")
        .populate("teacher", "fullName employeeId")
        .sort({ date: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Attendance.countDocuments(filterQuery);

      res.status(200).json({
        success: true,
        message: "Attendance retrieved successfully",
        data: {
          attendance: attendance.map(a => {
            const aDoc = a as any;
            return {
              id: a.id,
              student: aDoc.student
                ? {
                    id: aDoc.student.id || aDoc.student._id,
                    fullName: aDoc.student.fullName,
                    studentId: aDoc.student.studentId,
                    profilePicture: aDoc.student.profilePicture,
                  }
                : null,
              status: a.status,
              remarks: a.remarks,
              date: a.date,
              markedAt: a.markedAt,
            };
          }),
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get class attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get attendance statistics for a class
   * @route GET /api/v1/school/attendance/statistics
   * @access Private (School Admin, Teacher)
   */
  async getAttendanceStatistics(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const { classId, startDate, endDate } = req.query;
      const schoolId = req.user?.schoolId;

      if (!schoolId || !classId) {
        res.status(400).json({
          success: false,
          message: "School ID and Class ID are required",
        } as APIResponse);
        return;
      }

      const stats = await Attendance.getStatistics(
        new mongoose.Types.ObjectId(classId as string),
        undefined,
        startDate ? new Date(startDate as string) : undefined,
        endDate ? new Date(endDate as string) : undefined,
      );

      res.status(200).json({
        success: true,
        message: "Attendance statistics retrieved successfully",
        data: {
          statistics: stats,
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get attendance statistics error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve statistics",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get student attendance (Student Mobile App)
   * @route GET /api/v1/student/attendance
   * @access Private (Student)
   */
  async getStudentAttendance(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, startDate, endDate, page = 1, limit = 50 } = req.query;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        studentId: student._id,
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (startDate || endDate) {
        filterQuery.date = {};
        if (startDate) {
          filterQuery.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.date.$lte = new Date(endDate as string);
        }
      }

      const pageNum = Number.parseInt(page as string, 10);
      const limitNum = Number.parseInt(limit as string, 10);
      const skip = (pageNum - 1) * limitNum;

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const attendance = await Attendance.find(filterQuery)
        .populate("class", "className courseCode courseName")
        .sort({ date: -1 })
        .skip(skip)
        .limit(limitNum);

      const total = await Attendance.countDocuments(filterQuery);

      // Get statistics
      const stats = await Attendance.getStatistics(
        classId ? new mongoose.Types.ObjectId(classId as string) : undefined,
        student._id,
        startDate ? new Date(startDate as string) : undefined,
        endDate ? new Date(endDate as string) : undefined,
      );

      res.status(200).json({
        success: true,
        message: "Attendance retrieved successfully",
        data: {
          attendance: attendance.map(a => {
            const aDoc = a as any;
            return {
              id: a.id,
              class: aDoc.class
                ? {
                    id: aDoc.class.id || aDoc.class._id,
                    className: aDoc.class.className,
                    courseCode: aDoc.class.courseCode,
                    courseName: aDoc.class.courseName,
                  }
                : null,
              status: a.status,
              remarks: a.remarks,
              date: a.date,
            };
          }),
          statistics: stats,
          pagination: {
            page: pageNum,
            limit: limitNum,
            total,
            pages: Math.ceil(total / limitNum),
          },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get student attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Get teacher's class attendance (Teacher Mobile App)
   * @route GET /api/v1/teacher/attendance
   * @access Private (Teacher)
   */
  async getTeacherAttendance(req: AuthRequest, res: Response): Promise<void> {
    try {
      const { classId, date, startDate, endDate } = req.query;
      const userId = req.user?.userId;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "User not authenticated",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      });

      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
        } as APIResponse);
        return;
      }

      const filterQuery: any = {
        teacherId: teacher._id,
      };

      if (classId) {
        filterQuery.classId = new mongoose.Types.ObjectId(classId as string);
      }

      if (date) {
        const attendanceDate = new Date(date as string);
        attendanceDate.setHours(0, 0, 0, 0);
        const nextDay = new Date(attendanceDate);
        nextDay.setDate(nextDay.getDate() + 1);
        filterQuery.date = { $gte: attendanceDate, $lt: nextDay };
      } else if (startDate || endDate) {
        filterQuery.date = {};
        if (startDate) {
          filterQuery.date.$gte = new Date(startDate as string);
        }
        if (endDate) {
          filterQuery.date.$lte = new Date(endDate as string);
        }
      }

      // eslint-disable-next-line unicorn/no-array-callback-reference
      const attendance = await Attendance.find(filterQuery)
        .populate("class", "className courseCode")
        .populate("student", "fullName studentId")
        .sort({ date: -1 });

      res.status(200).json({
        success: true,
        message: "Attendance retrieved successfully",
        data: {
          attendance: attendance.map(a => {
            const aDoc = a as any;
            return {
              id: a.id,
              class: aDoc.class
                ? {
                    id: aDoc.class.id || aDoc.class._id,
                    className: aDoc.class.className,
                    courseCode: aDoc.class.courseCode,
                  }
                : null,
              student: aDoc.student
                ? {
                    id: aDoc.student.id || aDoc.student._id,
                    fullName: aDoc.student.fullName,
                    studentId: aDoc.student.studentId,
                  }
                : null,
              status: a.status,
              remarks: a.remarks,
              date: a.date,
            };
          }),
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Get teacher attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to retrieve attendance",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  // --- Compatibility endpoints for routes expecting these methods ---

  async teacherCheckIn(req: AuthRequest, res: Response): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Teacher check-in recorded",
    } as APIResponse);
  }

  async teacherCheckOut(req: AuthRequest, res: Response): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Teacher check-out recorded",
    } as APIResponse);
  }

  async getTeacherAttendanceLog(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Teacher attendance log",
      data: { logs: [] },
    } as APIResponse);
  }

  /**
   * Student check-in for a class
   * @route POST /api/v1/student/check-in
   * @body class_id, check_in_time (optional), location (optional: latitude, longitude)
   */
  async studentCheckIn(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const { class_id, check_in_time, location } = req.body || {};

      if (!class_id || typeof class_id !== "string") {
        res.status(400).json({
          success: false,
          message: "class_id is required and must be a string",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(class_id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class_id format",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (
        location != null &&
        (typeof location !== "object" ||
          typeof location.latitude !== "number" ||
          typeof location.longitude !== "number")
      ) {
        res.status(400).json({
          success: false,
          message:
            "location must be an object with latitude and longitude as numbers",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).lean();
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(class_id);
      const classItem = await Class.findById(classObjectId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const studentObjectId = student._id;
      const isEnrolled = classItem.studentIds?.some(
        (sid: mongoose.Types.ObjectId) =>
          sid.toString() === studentObjectId.toString(),
      );
      if (!isEnrolled) {
        res.status(403).json({
          success: false,
          message: "You are not enrolled in this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const timestamp = check_in_time ? new Date(check_in_time) : new Date();
      if (Number.isNaN(timestamp.getTime())) {
        res.status(400).json({
          success: false,
          message: "check_in_time must be a valid date/datetime",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      // 1. Check-in only during scheduled class time
      const session = getClassSessionForDate(classItem, timestamp);
      if (!session) {
        res.status(400).json({
          success: false,
          message: "Check-in is only allowed during the scheduled class time.",
          error: "Check-in is only allowed during the scheduled class time.",
        } as APIResponse);
        return;
      }
      if (timestamp < session.sessionStart || timestamp > session.sessionEnd) {
        res.status(400).json({
          success: false,
          message: "Check-in is only allowed during the scheduled class time.",
          error: "Check-in is only allowed during the scheduled class time.",
        } as APIResponse);
        return;
      }

      // 2. Single check-in per day: reject if already checked in (or already checked in and out)
      const { start: dayStart, end: dayEnd } = getDayBounds(timestamp);
      const existingCheckIn = await AttendanceLog.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        classId: classObjectId,
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
        timestamp: { $gte: dayStart, $lte: dayEnd },
      });
      if (existingCheckIn) {
        const hasCheckOutToday = await AttendanceLog.exists({
          userId: new mongoose.Types.ObjectId(userId),
          classId: classObjectId,
          userRole: "student",
          logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
          timestamp: { $gte: dayStart, $lte: dayEnd },
        });
        const message = hasCheckOutToday
          ? "You have already checked in and checked out for this class session. Only one check-in and one check-out per day are allowed."
          : "You have already checked in for this class session. Only one check-in per day is allowed.";
        res.status(409).json({
          success: false,
          message,
          error: message,
        } as APIResponse);
        return;
      }

      // 3. Attendance status: within 15 min of start = Present, else Late
      const minutesFromStart =
        timestamp.getTime() - session.sessionStart.getTime();
      const attendanceStatus =
        minutesFromStart <= LATE_GRACE_MS
          ? ATTENDANCE_STATUS_CHECK_IN.PRESENT
          : ATTENDANCE_STATUS_CHECK_IN.LATE;

      const locationStr =
        location != null &&
        typeof location.latitude === "number" &&
        typeof location.longitude === "number"
          ? `${location.latitude},${location.longitude}`
          : undefined;

      const userObjectId = new mongoose.Types.ObjectId(userId);
      const schoolId = classItem.schoolId;
      const semesterId = classItem.semesterId;

      const log = new AttendanceLog({
        schoolId,
        classId: classObjectId,
        semesterId,
        userId: userObjectId,
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
        timestamp,
        attendanceStatus,
        location: locationStr,
      });
      await log.save();

      logger.info("Student check-in recorded", {
        userId,
        classId: class_id,
        timestamp: log.timestamp,
        attendanceStatus,
      });

      res.status(201).json({
        success: true,
        message: "Student check-in recorded",
        data: {
          id: log._id,
          class_id,
          check_in_time: log.timestamp,
          attendance_status: attendanceStatus,
          location:
            locationStr == null
              ? undefined
              : {
                  latitude: Number.parseFloat(locationStr.split(",")[0]),
                  longitude: Number.parseFloat(locationStr.split(",")[1]),
                },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Student check-in error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to record check-in",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  /**
   * Student check-out from a class. Location is not required.
   * @route POST /api/v1/student/check-out
   * @body class_id, check_out_time (optional), location (optional: latitude, longitude)
   */
  async studentCheckOut(req: AuthRequest, res: Response): Promise<void> {
    try {
      const userId = req.user?.userId;
      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      const { class_id, check_out_time, location } = req.body || {};

      if (!class_id || typeof class_id !== "string") {
        res.status(400).json({
          success: false,
          message: "class_id is required and must be a string",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      if (!mongoose.Types.ObjectId.isValid(class_id)) {
        res.status(400).json({
          success: false,
          message: "Invalid class_id format",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      if (
        location != null &&
        (typeof location !== "object" ||
          typeof location.latitude !== "number" ||
          typeof location.longitude !== "number")
      ) {
        res.status(400).json({
          success: false,
          message:
            "location must be an object with latitude and longitude as numbers",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      const student = await Student.findOne({
        userId: new mongoose.Types.ObjectId(userId),
      }).lean();
      if (!student) {
        res.status(404).json({
          success: false,
          message: "Student profile not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      const classObjectId = new mongoose.Types.ObjectId(class_id);
      const classItem = await Class.findById(classObjectId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const studentObjectId = student._id;
      const isEnrolled = classItem.studentIds?.some(
        (sid: mongoose.Types.ObjectId) =>
          sid.toString() === studentObjectId.toString(),
      );
      if (!isEnrolled) {
        res.status(403).json({
          success: false,
          message: "You are not enrolled in this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const timestamp = check_out_time ? new Date(check_out_time) : new Date();
      if (Number.isNaN(timestamp.getTime())) {
        res.status(400).json({
          success: false,
          message: "check_out_time must be a valid date/datetime",
          error: "Validation failed",
        } as APIResponse);
        return;
      }

      // Check-out only after check-in; single check-out per day
      const { start: dayStart, end: dayEnd } = getDayBounds(timestamp);

      // Reject if already checked out for this class on this date
      const existingCheckOut = await AttendanceLog.exists({
        userId: new mongoose.Types.ObjectId(userId),
        classId: classObjectId,
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
        timestamp: { $gte: dayStart, $lte: dayEnd },
      });
      if (existingCheckOut) {
        res.status(409).json({
          success: false,
          message:
            "You have already checked out for this class session. Only one check-out per day is allowed.",
          error:
            "You have already checked out for this class session. Only one check-out per day is allowed.",
        } as APIResponse);
        return;
      }

      // Must have checked in first for this class session (same day)
      const latestCheckIn = await AttendanceLog.findOne({
        userId: new mongoose.Types.ObjectId(userId),
        classId: classObjectId,
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_IN,
        timestamp: { $gte: dayStart, $lte: dayEnd },
      }).sort({ timestamp: -1 });

      if (!latestCheckIn) {
        res.status(400).json({
          success: false,
          message:
            "You have not checked in for this class session. Check-in is required before check-out.",
          error:
            "You have not checked in for this class session. Check-in is required before check-out.",
        } as APIResponse);
        return;
      }

      const locationStr =
        location != null &&
        typeof location.latitude === "number" &&
        typeof location.longitude === "number"
          ? `${location.latitude},${location.longitude}`
          : undefined;

      const userObjectId = new mongoose.Types.ObjectId(userId);
      const schoolId = classItem.schoolId;
      const semesterId = classItem.semesterId;

      const log = new AttendanceLog({
        schoolId,
        classId: classObjectId,
        semesterId,
        userId: userObjectId,
        userRole: "student",
        logType: ATTENDANCE_LOG_TYPE.CHECK_OUT,
        timestamp,
        location: locationStr,
      });
      await log.save();

      logger.info("Student check-out recorded", {
        userId,
        classId: class_id,
        timestamp: log.timestamp,
      });

      res.status(201).json({
        success: true,
        message: "Student check-out recorded",
        data: {
          id: log._id,
          class_id,
          check_out_time: log.timestamp,
          location:
            locationStr == null
              ? undefined
              : {
                  latitude: Number.parseFloat(locationStr.split(",")[0]),
                  longitude: Number.parseFloat(locationStr.split(",")[1]),
                },
        },
      } as APIResponse);
    } catch (error: any) {
      logger.error("Student check-out error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to record check-out",
        error: error.message || "Internal server error",
      } as APIResponse);
    }
  }

  async getStudentAttendanceLog(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Student attendance log",
      data: { logs: [] },
    } as APIResponse);
  }

  async getStudentAttendanceLogForTeacher(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Student attendance log (teacher view)",
      data: { logs: [] },
    } as APIResponse);
  }

  /**
   * Get attendance history for a student in a specific class (Teacher view)
   * @route GET /api/v1/teacher/classes/:classId/students/:id/attendance
   * @access Private (Teacher)
   * @query page, limit
   */
  async getStudentClassAttendanceForTeacher(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    try {
      const userId = req.user?.userId;
      const { classId, id: studentId } = req.params;
      const { page = 1, limit = 20 } = req.query;

      if (!userId) {
        res.status(401).json({
          success: false,
          message: "Unauthorized",
          error: "Invalid request",
        } as APIResponse);
        return;
      }

      if (
        !mongoose.Types.ObjectId.isValid(classId) ||
        !mongoose.Types.ObjectId.isValid(studentId)
      ) {
        res.status(400).json({
          success: false,
          message: "Invalid class ID or student ID",
          error: "Invalid ID format",
        } as APIResponse);
        return;
      }

      const teacher = await Teacher.findOne({ userId });
      if (!teacher) {
        res.status(404).json({
          success: false,
          message: "Teacher profile not found",
          error: "Teacher not found",
        } as APIResponse);
        return;
      }

      const classItem = await Class.findById(classId).lean();
      if (!classItem) {
        res.status(404).json({
          success: false,
          message: "Class not found",
          error: "Class not found",
        } as APIResponse);
        return;
      }

      const teacherObjectId = teacher._id;
      const isTeacherAssigned = (classItem.teacherIds as any[])?.some(
        (t: mongoose.Types.ObjectId) =>
          t.toString() === teacherObjectId.toString(),
      );
      if (!isTeacherAssigned) {
        res.status(403).json({
          success: false,
          message: "You are not assigned to this class",
          error: "Access denied",
        } as APIResponse);
        return;
      }

      const studentObjectId = new mongoose.Types.ObjectId(studentId);
      const isEnrolled = (classItem.studentIds as any[])?.some(
        (s: mongoose.Types.ObjectId) => s.toString() === studentId,
      );
      if (!isEnrolled) {
        res.status(404).json({
          success: false,
          message: "Student is not enrolled in this class",
          error: "Student not enrolled",
        } as APIResponse);
        return;
      }

      const student = await Student.findById(studentObjectId)
        .select("userId")
        .lean();
      if (!student || !student.userId) {
        res.status(404).json({
          success: false,
          message: "Student not found",
          error: "Student not found",
        } as APIResponse);
        return;
      }

      // Class must be active for attendance to be returned
      const isClassActive = classItem.status === "active" && classItem.isActive;
      if (!isClassActive) {
        res.status(200).json({
          success: true,
          message: "Attendance records fetched successfully",
          data: {
            studentId,
            classId,
            attendance: [],
          },
        } as APIResponse);
        return;
      }

      // Get student join date for this class (Enrollment, or fallback to class creation)
      const classObjectId = new mongoose.Types.ObjectId(classId);
      const enrollment = await Enrollment.findOne({
        studentId: studentObjectId,
        courseOfferingId: classObjectId,
      })
        .select("enrollmentDate")
        .lean();

      let joinDate: Date;
      if (enrollment?.enrollmentDate) {
        joinDate = new Date(enrollment.enrollmentDate);
      } else {
        // Fallback: student is in class.studentIds but no Enrollment record
        joinDate = classItem.createdAt
          ? new Date(classItem.createdAt)
          : new Date();
      }
      joinDate.setHours(0, 0, 0, 0);

      // Build set of active schedule days (lowercase)
      const schedule = (classItem.schedule || []) as {
        day: string;
        isActive?: boolean;
      }[];
      const activeScheduleDays = new Set(
        schedule.filter(s => s.isActive === true).map(s => s.day.toLowerCase()),
      );

      if (activeScheduleDays.size === 0) {
        res.status(200).json({
          success: true,
          message: "Attendance records fetched successfully",
          data: {
            studentId,
            classId,
            attendance: [],
          },
        } as APIResponse);
        return;
      }

      // Day names for JS Date.getDay(): 0=Sunday, 1=Monday, ..., 6=Saturday
      const DAY_NAMES = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];

      // Use UTC for date grouping so it matches check-in/check-out API (ISO/UTC)
      const dateKey = (d: Date): string => {
        const x = new Date(d);
        return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, "0")}-${String(x.getUTCDate()).padStart(2, "0")}`;
      };

      // Generate valid class days from join date to today in UTC (only scheduled days)
      const now = new Date();
      const todayUTC = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );
      const validDates: string[] = [];
      const joinUTC = new Date(joinDate);
      const joinUTCStart = new Date(
        Date.UTC(
          joinUTC.getUTCFullYear(),
          joinUTC.getUTCMonth(),
          joinUTC.getUTCDate(),
        ),
      );
      const cursor = new Date(joinUTCStart.getTime());
      while (cursor <= todayUTC) {
        const dayName = DAY_NAMES[cursor.getUTCDay()];
        if (activeScheduleDays.has(dayName)) {
          validDates.push(dateKey(cursor));
        }
        cursor.setUTCDate(cursor.getUTCDate() + 1);
      }

      const studentUserId = student.userId;

      const todayEndUTC = new Date(
        Date.UTC(
          now.getUTCFullYear(),
          now.getUTCMonth(),
          now.getUTCDate(),
          23,
          59,
          59,
          999,
        ),
      );

      const [logs, breaks, manualAttendance] = await Promise.all([
        AttendanceLog.find({
          userId: studentUserId,
          classId: classObjectId,
          userRole: "student",
        })
          .sort({ timestamp: 1 })
          .lean(),
        BreakTimer.find({
          userId: studentUserId,
          classId: classObjectId,
          userRole: "student",
          status: "completed",
          endedAt: { $exists: true, $ne: null },
        })
          .sort({ startedAt: 1 })
          .lean(),
        Attendance.find({
          classId: classObjectId,
          studentId: studentObjectId,
          status: ATTENDANCE_STATUS.PRESENT,
          date: { $gte: joinUTCStart, $lte: todayEndUTC },
        })
          .select("_id date")
          .lean(),
      ]);

      // Return ISO 8601 timestamps to match check-in/check-out API format
      const toISO = (d: Date): string => new Date(d).toISOString();

      /** Parse "lat,lng" string to { latitude, longitude } or null */
      const parseLocation = (
        loc?: string | null,
      ): { latitude: number; longitude: number } | null => {
        if (!loc || typeof loc !== "string") return null;
        const parts = loc.split(",").map(s => s.trim());
        if (parts.length !== 2) return null;
        const lat = Number.parseFloat(parts[0]);
        const lng = Number.parseFloat(parts[1]);
        if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
        return { latitude: lat, longitude: lng };
      };

      const byDate: Record<
        string,
        {
          attendanceId?: string | null;
          classIn?: string;
          classOut?: string;
          checkInLocation?: string | null;
          attendanceStatus?: string | null;
          breaks: { breakIn: string; breakOut: string }[];
        }
      > = {};

      for (const log of logs) {
        const key = dateKey(log.timestamp);
        if (!byDate[key]) byDate[key] = { breaks: [] };
        if (log.logType === ATTENDANCE_LOG_TYPE.CHECK_IN) {
          if (!byDate[key].classIn) {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            byDate[key].attendanceId = log._id == null ? null : String(log._id);
            byDate[key].classIn = toISO(log.timestamp);
            byDate[key].checkInLocation = log.location ?? null;
            byDate[key].attendanceStatus =
              (log as { attendanceStatus?: string }).attendanceStatus ?? null;
          }
        } else if (log.logType === ATTENDANCE_LOG_TYPE.CHECK_OUT) {
          byDate[key].classOut = toISO(log.timestamp);
        }
      }

      for (const b of breaks) {
        if (!b.startedAt || !b.endedAt) continue;
        const key = dateKey(b.startedAt);
        if (!byDate[key]) byDate[key] = { breaks: [] };
        byDate[key].breaks.push({
          breakIn: toISO(b.startedAt),
          breakOut: toISO(b.endedAt),
        });
      }

      for (const att of manualAttendance) {
        const key = dateKey(att.date);
        const session = getClassSessionForDateUTC(classItem, key);
        if (session) {
          const existing = byDate[key];
          byDate[key] = {
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/restrict-template-expressions
            attendanceId: att._id == null ? null : String(att._id),
            classIn: toISO(session.sessionStart),
            classOut: toISO(session.sessionEnd),
            checkInLocation: null,
            attendanceStatus: "present",
            breaks: existing?.breaks ?? [],
          };
        }
      }

      // Include both scheduled days and any day that has log data so check-in/check-out always appears
      const datesWithLogs = Object.keys(byDate);
      const allDatesSet = new Set<string>([...validDates, ...datesWithLogs]);
      const sortedValidDates = [...allDatesSet].sort((a, b) =>
        b.localeCompare(a),
      );
      const pageNum = Math.max(1, Number(page));
      const limitNum = Math.min(100, Math.max(1, Number(limit)));
      const skip = (pageNum - 1) * limitNum;
      const totalCount = sortedValidDates.length;
      const paginatedDates = sortedValidDates.slice(skip, skip + limitNum);

      const semesterLocked = await isSemesterLockedForClass(classObjectId);

      const auditLogs = await AttendanceAudit.find({
        classId: classObjectId,
        studentId: studentObjectId,
      })
        .sort({ createdAt: -1 })
        .lean();

      const auditByDate: Record<
        string,
        {
          changedBy: string;
          changedByRole: string;
          fromStatus?: string;
          toStatus: string;
          changedAt: Date;
          reason?: string;
        }[]
      > = {};

      for (const log of auditLogs) {
        const key = dateKey(log.date);
        if (!auditByDate[key]) auditByDate[key] = [];
        auditByDate[key].push({
          changedBy: log.changedBy?.toString(),
          changedByRole: log.changedByRole,
          fromStatus: log.fromStatus,
          toStatus: log.toStatus,
          changedAt: log.createdAt,
          reason: log.reason,
        });
      }

      const attendance = paginatedDates.map(date => {
        const hasCheckIn = Boolean(byDate[date]?.classIn);
        const editable = resolveEditableState(hasCheckIn, semesterLocked);
        return {
          attendanceId: byDate[date]?.attendanceId ?? null,
          date,
          classIn: byDate[date]?.classIn ?? null,
          classOut: byDate[date]?.classOut ?? null,
          checkInLocation: parseLocation(byDate[date]?.checkInLocation) ?? null,
          attendanceStatus: byDate[date]?.attendanceStatus ?? null,
          breaks: byDate[date]?.breaks || [],
          isEditable: editable.isEditable,
          editableReason: editable.editableReason,
          auditLog: auditByDate[date] ?? [],
        };
      });

      res.status(200).json({
        success: true,
        message: "Attendance records fetched successfully",
        data: {
          studentId,
          classId,
          attendance,
          pagination: {
            currentPage: pageNum,
            totalPages: Math.ceil(totalCount / limitNum),
            totalCount,
            limit: limitNum,
            hasNextPage: pageNum < Math.ceil(totalCount / limitNum),
            hasPrevPage: pageNum > 1,
          },
        },
      } as APIResponse);
    } catch (error) {
      logger.error("Get student class attendance error:", error);
      res.status(500).json({
        success: false,
        message: "Failed to fetch attendance records",
        error: "Internal server error",
      } as APIResponse);
    }
  }

  async getSchoolStudentAttendance(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    res.status(200).json({
      success: true,
      message: "School student attendance",
      data: { attendance: [] },
    } as APIResponse);
  }

  async getAdminSchoolAttendance(
    req: AuthRequest,
    res: Response,
  ): Promise<void> {
    res.status(200).json({
      success: true,
      message: "Admin school attendance",
      data: { attendance: [] },
    } as APIResponse);
  }
}

export const attendanceController = new AttendanceController();
